import { cn } from "@exhousing/design-system";
import {
  Bell,
  BookOpen,
  Building,
  Building2,
  CalendarCheck,
  ClipboardCheck,
  FileText,
  Home,
  Inbox,
  LayoutDashboard,
  Mail,
  MailQuestion,
  Plus,
  Receipt,
  Search,
  Send,
  Settings,
  Stamp,
  Target,
  UserCog,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useMe } from "@/hooks/api/useAuth";
import { isLandlord } from "@/lib/roles";
import { useProperties } from "@/hooks/api/useProperties";

type Me = ReturnType<typeof useMe>["data"];
type Cmd = {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon: ComponentType<{ className?: string }>;
  to: string;
  keywords?: string;
  show?: (me: Me) => boolean;
};

const isSuperAdmin = (me: Me) => Boolean(me?.roles?.includes("super_admin"));
const canManageUsers = (me: Me) =>
  Boolean(me?.permissions?.includes("user.view") || isSuperAdmin(me));
const canViewCrm = (me: Me) =>
  Boolean(me?.permissions?.includes("crm.view") || isSuperAdmin(me));
const canViewDocuments = (me: Me) =>
  Boolean(me?.permissions?.includes("document.view") || isSuperAdmin(me));
const canViewApplications = (me: Me) =>
  Boolean(me?.permissions?.includes("application.view") || isSuperAdmin(me));
const canViewBilling = (me: Me) =>
  Boolean(me?.permissions?.includes("billing.view") || isSuperAdmin(me));
const canViewInbox = (me: Me) =>
  Boolean(me?.permissions?.includes("inbox.view") || isSuperAdmin(me));

// Mirrors the sidebar IA. Items demoted from the sidebar (Ledger, Invoices, Inbox,
// Letterhead) live here so they stay one ⌘K away.
const STATIC: Cmd[] = [
  // Quick create
  { id: "new-property", label: "New property", group: "Create", icon: Plus, to: "/properties?new=1", keywords: "add listing create" },
  // Workspace
  { id: "nav-dashboard", label: "Dashboard", group: "Go to", icon: LayoutDashboard, to: "/dashboard" },
  { id: "nav-properties", label: "Properties", group: "Go to", icon: Home, to: "/properties" },
  { id: "nav-applications", label: "Applications", group: "Go to", icon: MailQuestion, to: "/applications", show: canViewApplications },
  { id: "nav-viewings", label: "Viewings", group: "Go to", icon: CalendarCheck, to: "/viewings" },
  { id: "nav-contracts", label: "Contracts", group: "Go to", icon: Building2, to: "/contracts" },
  { id: "nav-inspections", label: "Inspections", group: "Go to", icon: ClipboardCheck, to: "/inspections" },
  // Finance
  { id: "nav-invoices", label: "Invoices", group: "Go to", icon: Receipt, to: "/invoices", show: canViewBilling },
  { id: "nav-ledger", label: "Ledger", group: "Go to", icon: BookOpen, to: "/ledger", show: canViewBilling, keywords: "accounting journal" },
  // Marketing
  { id: "nav-subscribers", label: "Subscribers", group: "Go to", icon: Mail, to: "/subscribers" },
  { id: "nav-mailings", label: "Mailings", group: "Go to", icon: Send, to: "/mailings" },
  { id: "nav-crm", label: "CRM", group: "Go to", icon: Target, to: "/crm", show: canViewCrm },
  { id: "nav-inbox", label: "Inbox", group: "Go to", icon: Inbox, to: "/inbox", show: canViewInbox },
  // Directory
  { id: "nav-parties", label: "Landlords & Tenants", group: "Go to", icon: Users, to: "/parties" },
  // Settings
  { id: "nav-settings", label: "Settings", group: "Go to", icon: Settings, to: "/settings", keywords: "config preferences" },
  { id: "nav-templates", label: "Document templates", group: "Go to", icon: FileText, to: "/document-templates", show: canViewDocuments },
  { id: "nav-notifications", label: "Notifications", group: "Go to", icon: Bell, to: "/notifications", show: canViewDocuments },
  { id: "nav-letterhead", label: "Letterhead", group: "Go to", icon: Stamp, to: "/letterhead", show: canManageUsers },
  { id: "nav-team", label: "Team", group: "Go to", icon: UserCog, to: "/team", show: canManageUsers },
  { id: "nav-agencies", label: "Agencies", group: "Go to", icon: Building, to: "/agencies", show: isSuperAdmin },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: me } = useMe();
  // Shares the cached property list used by the Properties page.
  const { data: properties } = useProperties();

  // Global ⌘K / Ctrl+K toggle + Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-command-palette", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-command-palette", onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // focus after paint
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const results = useMemo<Cmd[]>(() => {
    const q = query.trim().toLowerCase();
    const landlordRoutes = ["/dashboard", "/properties", "/applications", "/contracts"];
    const scoped = isLandlord(me)
      ? STATIC.filter(
          (c) =>
            c.group !== "Create" &&
            (!c.to || landlordRoutes.some((r) => c.to?.startsWith(r))),
        )
      : STATIC;
    const nav = scoped.filter((c) => !c.show || c.show(me)).filter((c) => {
      if (!q) return true;
      return `${c.label} ${c.keywords ?? ""}`.toLowerCase().includes(q);
    });
    const props: Cmd[] = q
      ? (properties ?? [])
          .map((p) => {
            const addr =
              [p.street, p.house_number].filter(Boolean).join(" ") ||
              p.reference ||
              "Untitled property";
            return { p, addr };
          })
          .filter(({ addr, p }) =>
            `${addr} ${p.city ?? ""}`.toLowerCase().includes(q),
          )
          .slice(0, 6)
          .map(({ p, addr }) => ({
            id: `prop-${p.id}`,
            label: addr,
            hint: p.city ?? undefined,
            group: "Properties",
            icon: Home,
            to: `/properties/${p.id}`,
          }))
      : [];
    return [...props, ...nav];
  }, [query, me, properties]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, results.length - 1)));
  }, [results.length]);

  if (!open) return null;

  const go = (cmd: Cmd | undefined) => {
    if (!cmd) return;
    setOpen(false);
    navigate(cmd.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    }
  };

  // Render grouped, but keep a flat index for keyboard selection.
  let flatIndex = -1;
  const groups: { label: string; items: { cmd: Cmd; index: number }[] }[] = [];
  for (const cmd of results) {
    flatIndex += 1;
    const last = groups[groups.length - 1];
    const entry = { cmd, index: flatIndex };
    if (last && last.label === cmd.group) last.items.push(entry);
    else groups.push({ label: cmd.group, items: [entry] });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-foreground/30 px-4 pt-[12vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search properties, jump to a section, or create…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground sm:block">
            ESC
          </kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto py-2">
          {results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              No matches.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.label} className="px-2 pb-1">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </p>
                {group.items.map(({ cmd, index }) => (
                  <button
                    key={cmd.id}
                    type="button"
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(cmd)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm",
                      index === active
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-muted",
                    )}
                  >
                    <cmd.icon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{cmd.label}</span>
                    {cmd.hint && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {cmd.hint}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
